import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getShiftById, updateShift } from "adminApi/DutyRosterApi/ShiftApi";
import { validateForm } from "utils/validation";
import { ShiftRules } from "validation/ValidationRules";
import Spinner from "Common/Globle/Spinner";
import fields from "./json/ShiftFields.json";
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";
import ShiftForm from "./Components/ShiftForm";
import useFetchAllDataByID from "Hooks/useFetchAllDataById";

const EditShift = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { formFields } = fields;

    const initialValues = formFields.reduce((acc, field) => {
        acc[field.name] = "";
        return acc;
    }, {});

    const {
        values,
        errors,
        handleChange,
        setErrors,
        setValues
    } = useFormChange(initialValues, ShiftRules);

    const { data: shiftData, loading: loadingFetch } = useFetchAllDataByID(getShiftById, id);

    useEffect(() => {
        if (shiftData) {
            setValues({
                shift_name: shiftData.shift_name,
                start_time: shiftData.start_time,
                end_time: shiftData.end_time,
                shift_hours: shiftData.shift_hours
            });
        }
    }, [shiftData, setValues]);

    useEffect(() => {
        if (values.start_time && values.end_time) {
            const [startH, startM, startS] = values.start_time.split(':').map(Number);
            const [endH, endM, endS] = values.end_time.split(':').map(Number);

            const startDate = new Date();
            startDate.setHours(startH || 0, startM || 0, startS || 0, 0);

            const endDate = new Date();
            endDate.setHours(endH || 0, endM || 0, endS || 0, 0);

            if (endDate < startDate) {
                endDate.setDate(endDate.getDate() + 1);
            }

            const diffMs = endDate - startDate;
            const diffMinutes = diffMs / (1000 * 60);
            const diffHrs = diffMs / (1000 * 60 * 60);
            const shiftHours = parseFloat(diffHrs.toFixed(2));

            // Validate minimum 30 minutes gap
            if (diffMinutes < 30) {
                setErrors((prev) => ({
                    ...prev,
                    end_time: "End time must be at least 30 minutes after start time"
                }));
            } else {
                // Clear the error if valid
                setErrors((prev) => {
                    const { end_time, ...rest } = prev;
                    return rest;
                });
            }

            // Prevent infinite loop if already correctly calculated
            // and allow initial fetch values to render without overriding unless necessary.
            if (values.shift_hours !== shiftHours) {
                setValues((prev) => ({ ...prev, shift_hours: shiftHours }));
            }
        } else {
            if (values.shift_hours !== "") {
                setValues((prev) => ({ ...prev, shift_hours: "" }));
            }
        }
    }, [values.start_time, values.end_time, values.shift_hours, setValues, setErrors]);

    const {
        loading: loadingSubmit,
        handleSubmit
    } = useFormSubmit(
        async (formValues) => {
            return await updateShift({ id, data: formValues });
        },
        (vals) => {
            const err = validateForm(vals, ShiftRules);
            // Check for time gap error in current errors state
            if (errors.end_time) {
                err.end_time = errors.end_time;
            }
            setErrors(err);
            return err;
        },
        () => navigate(-1),
        "Shift updated successfully"
    );

    return (
        <>
            {(loadingSubmit || loadingFetch) && <Spinner isSpinner={true} />}
            <ShiftForm
                fields={formFields}
                values={values}
                errors={errors}
                handleChange={handleChange}
                handleSubmit={(e) => handleSubmit(e, values)}
                loading={loadingSubmit}
                title="Edit Shift"
                isEdit={true}
            />
        </>
    );
};

export default EditShift;
